import { CircleHelp, Plus, RefreshCw, Search } from "lucide-react";
import { useMemo, useState } from "react";

import {
  useJDQuestions,
  usePromoteJDQuestions,
  useRematchJD,
  type JDQuestion,
} from "@/api/jd-question-queries";
import type { JDDetail } from "@/api/jd-queries";
import { useCan } from "@/auth/member";
import { AddQuestionsDrawer } from "@/components/jd-add-questions";
import { DraftsPanel } from "@/components/jd-drafts-panel";
import { MatchedGroup, OWN_GRID, OwnRow } from "@/components/jd-question-rows";
import { Tip } from "@/components/pipeline-stepper";
import { SearchableSelect } from "@/components/searchable-select";
import { Button } from "@/components/ui/button";
import { Input, Select } from "@/components/ui/input";
import { Empty, ErrorNote, Panel } from "@/components/ui/panel";
import { skillGroup } from "@/lib/jd-steps";
import { cn } from "@/lib/utils";

/**
 * Step 3, Questions — DESIGN §20.6, §20.7, §20.11. The JD's own questions (found in it, added since, or
 * drafted for a gap) and the bank questions matched to its skills, with the Resource Manager's levers:
 * pin (drawn first), exclude (never drawn), remove, and approve when they may. Drafts waiting for a
 * decision sit on top, since nothing else here moves until somebody decides on them.
 */

type Tab = "own" | "matched";
type State = "" | "live" | "waiting" | "on_way" | "excluded";

const ON_ITS_WAY = new Set(["raw", "extracted", "split_checked", "enriched", "embedded", "dedup_checked"]);

function stateOf(q: JDQuestion): State | "other" {
  if (q.is_excluded) return "excluded";
  if (q.status === "approved") return "live";
  if (q.status === "pending_review" || q.status === "stale") return "waiting";
  if (ON_ITS_WAY.has(q.status)) return "on_way";
  return "other";
}

// A stack_path is dotted ('dotnet.aspnetcore.webapi'); the filter offers its first segment.
const stackOf = (q: JDQuestion) => (q.stack_path ?? "").split(".")[0] ?? "";

export function QuestionsStep({ jd, canManage }: { jd: JDDetail; canManage: boolean }) {
  const query = useJDQuestions(jd.jd.id);
  const rematch = useRematchJD(jd.jd.id);
  const promote = usePromoteJDQuestions(jd.jd.id);
  const canReview = useCan("review");
  const [tab, setTab] = useState<Tab>("own");
  const [adding, setAdding] = useState(false);
  const [checked, setChecked] = useState<Set<number>>(new Set());
  const [search, setSearch] = useState("");
  const [stack, setStack] = useState("");
  const [concept, setConcept] = useState("");
  const [state, setState] = useState<State>("");
  const [sincePublish, setSincePublish] = useState(false);
  const [pinnedOnly, setPinnedOnly] = useState(false);
  const data = query.data;

  const { stacks, concepts } = useMemo(() => {
    const st = new Set<string>();
    const co = new Set<string>();
    for (const q of data?.items ?? []) {
      if (q.stack_path) st.add(stackOf(q));
      if (q.primary_concept) co.add(q.primary_concept);
      for (const c of q.sub_concepts) co.add(c);
    }
    return { stacks: [...st].sort(), concepts: [...co].sort() };
  }, [data]);

  const narrow = (q: JDQuestion) => {
    const needle = search.trim().toLowerCase();
    return (
      (!needle || q.question_text.toLowerCase().includes(needle)) &&
      (!stack || (q.stack_path !== null && stackOf(q) === stack)) &&
      (!concept || q.primary_concept === concept || q.sub_concepts.includes(concept))
    );
  };

  if (query.isLoading) return <p className="text-bank-muted text-sm">Loading…</p>;
  if (!data) return <ErrorNote error={query.error ?? "Could not load the questions."} />;

  const ownAll = data.items.filter((q) => q.origin !== "matched");
  const matchedAll = data.items.filter((q) => q.origin === "matched");
  const own = ownAll.filter(
    (q) => narrow(q) && (!state || stateOf(q) === state) && (!sincePublish || q.after_publish),
  );
  const matched = matchedAll.filter((q) => narrow(q) && (!pinnedOnly || q.is_pinned));
  const narrowed =
    search.trim() !== "" || stack !== "" || concept !== "" || state !== "" || sincePublish || pinnedOnly;

  // Promotion is a reviewer's decision about approved, JD-only questions (§20.7).
  const promotable = (q: JDQuestion) =>
    canReview && q.status === "approved" && q.scope === "jd_only" && q.canonical_id === null;
  const ticked = own.filter((q) => promotable(q) && checked.has(q.question_id)).map((q) => q.question_id);

  function toggle(id: number) {
    setChecked((current) => {
      const next = new Set(current);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  }

  // Matched questions under the skill they matched, in the order the Skills step lists them.
  const bySkill = new Map<number | null, JDQuestion[]>();
  for (const q of matched) bySkill.set(q.skill_id, [...(bySkill.get(q.skill_id) ?? []), q]);
  const groups = [
    ...jd.skills
      .filter((sk) => bySkill.has(sk.id))
      .map((sk) => ({ key: sk.id, sk, items: bySkill.get(sk.id) ?? [] })),
    ...(bySkill.has(null) ? [{ key: null, sk: undefined, items: bySkill.get(null) ?? [] }] : []),
  ];
  const nothingToMatch = jd.skills.filter((sk) => skillGroup(sk) === "attention").map((sk) => sk.name);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <p className="text-[13.5px]">
          <strong>{data.counts.own_served} live</strong>
          <span className="text-bank-muted"> · </span>
          <strong className={data.counts.own_waiting ? "text-bank-attention-ink" : undefined}>
            {data.counts.own_waiting} waiting for a reviewer
          </strong>
          <span className="text-bank-muted"> · </span>
          <strong>{data.counts.matched} matched</strong>
          <span className="text-bank-muted"> from the bank</span>
        </p>
        {canManage && (
          <div className="ml-auto flex items-center gap-2">
            <Button disabled={rematch.isPending} onClick={() => rematch.mutate()}>
              <RefreshCw className={cn("size-4", rematch.isPending && "animate-spin")} aria-hidden="true" />
              Re-match bank
            </Button>
            <span
              tabIndex={0}
              aria-label="What does Re-match bank do?"
              aria-describedby="rematch-tip"
              className="group text-bank-muted hover:text-bank-ink focus-visible:ring-bank-accent relative inline-flex cursor-help rounded-full focus-visible:ring-2 focus-visible:outline-none"
            >
              <CircleHelp className="size-4" aria-hidden="true" />
              <Tip
                id="rematch-tip"
                title="Re-match bank"
                what="Finds the bank questions closest to this JD's skills again. It runs on its own after the analysis, on publish and when a mapping is saved; press it after the bank has grown."
                ai="Pinned and excluded matches are kept."
                align="end"
              />
            </span>
            <Button variant="primary" onClick={() => setAdding(true)}>
              <Plus className="size-4" aria-hidden="true" />
              Add questions
            </Button>
          </div>
        )}
      </div>
      {rematch.data && (
        <p className="text-bank-muted text-xs">
          {rematch.data.job
            ? `Some skills need a vector first; matching runs as job #${rematch.data.job.job_id}.`
            : `${rematch.data.matched} matched: ${rematch.data.added} new, ${rematch.data.removed} no longer close enough.`}
        </p>
      )}
      <ErrorNote error={rematch.error ?? promote.error} />

      <DraftsPanel jdId={jd.jd.id} canManage={canManage} />

      <Panel className="overflow-hidden">
        <div
          role="tablist"
          aria-label="Which questions"
          className="border-bank-border flex gap-1 border-b px-3"
        >
          {(
            [
              ["own", "This JD's own", ownAll.length],
              ["matched", "Matched from the bank", matchedAll.length],
            ] as const
          ).map(([value, label, n]) => (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={tab === value}
              onClick={() => setTab(value)}
              className={cn(
                "-mb-px inline-flex items-center gap-1.5 border-b-2 px-3 py-2.5 text-[13.5px]",
                tab === value
                  ? "border-bank-accent font-semibold"
                  : "text-bank-muted hover:text-bank-ink border-transparent",
              )}
            >
              {label} <span className="font-mono text-[11.5px] tabular-nums">{n}</span>
            </button>
          ))}
        </div>

        <div className="border-bank-border flex flex-wrap items-center gap-2 border-b px-4 py-2.5">
          <label className="relative min-w-44 flex-[1_1_220px]">
            <Search className="text-bank-muted absolute top-2 left-2.5 size-4" aria-hidden="true" />
            <Input
              type="search"
              className="pl-8"
              placeholder="Search question text"
              aria-label="Search questions"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>
          <SearchableSelect
            className="w-44"
            label="Filter by stack"
            placeholder="All stacks"
            options={stacks}
            value={stack}
            onChange={setStack}
          />
          <SearchableSelect
            className="w-52"
            label="Filter by concept"
            placeholder="All concepts"
            options={concepts}
            value={concept}
            onChange={setConcept}
          />
          {tab === "own" ? (
            <>
              <Select
                aria-label="Filter by where it stands"
                className="w-40"
                value={state}
                onChange={(e) => setState(e.target.value as State)}
              >
                <option value="">Any state</option>
                <option value="live">Live</option>
                <option value="waiting">Waiting for a reviewer</option>
                <option value="on_way">On its way</option>
                <option value="excluded">Excluded</option>
              </Select>
              <label className="flex items-center gap-1.5 text-[13px]">
                <input
                  type="checkbox"
                  className="accent-bank-accent"
                  checked={sincePublish}
                  onChange={(e) => setSincePublish(e.target.checked)}
                />
                Added since publishing
              </label>
            </>
          ) : (
            <label className="flex items-center gap-1.5 text-[13px]">
              <input
                type="checkbox"
                className="accent-bank-accent"
                checked={pinnedOnly}
                onChange={(e) => setPinnedOnly(e.target.checked)}
              />
              Pinned only
            </label>
          )}
          {narrowed && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => (
                setSearch(""),
                setStack(""),
                setConcept(""),
                setState(""),
                setSincePublish(false),
                setPinnedOnly(false)
              )}
            >
              Clear
            </Button>
          )}
        </div>

        {tab === "own" &&
          (own.length === 0 ? (
            <Empty>
              {ownAll.length === 0
                ? "None yet. A JD needs none to be published: its share of a round then comes from matched questions."
                : "Nothing matches those filters."}
            </Empty>
          ) : (
            <div className="overflow-x-auto">
              <div
                className={cn(
                  OWN_GRID,
                  "bg-bank-surface-2 text-bank-muted px-4 py-2 font-mono text-[10.5px] tracking-[0.07em] uppercase",
                )}
              >
                <span />
                <span>Question</span>
                <span>Where it stands</span>
                <span>Goes to</span>
                <span>Source</span>
                <span className="text-right">Added</span>
              </div>
              {own.map((q) => (
                <OwnRow
                  key={q.question_id}
                  q={q}
                  jdId={jd.jd.id}
                  canManage={canManage}
                  selfApprove={data.rm_self_approve}
                  checked={promotable(q) ? checked.has(q.question_id) : null}
                  onCheck={() => toggle(q.question_id)}
                />
              ))}
            </div>
          ))}

        {tab === "matched" && (
          <>
            <p className="text-bank-muted px-4 py-2.5 text-[12.5px]">
              Bank questions closest to each skill. <strong className="text-bank-ink">Pin</strong> one to
              always ask it first; <strong className="text-bank-ink">exclude</strong> one to never ask it for
              this JD.
            </p>
            {groups.length === 0 ? (
              <Empty>
                {matchedAll.length === 0
                  ? "Nothing matched yet. Matching runs after the analysis and on publish; skills mapped to a stack with questions are what it matches against."
                  : "Nothing matches those filters."}
              </Empty>
            ) : (
              groups.map((g, i) => (
                <MatchedGroup
                  key={g.key ?? "none"}
                  title={g.sk?.name ?? "Other matches"}
                  meta={g.sk ? `${g.sk.stack_path ?? "keyword"} · ${g.sk.importance}` : ""}
                  items={g.items}
                  canManage={canManage}
                  defaultOpen={i === 0 || narrowed}
                />
              ))
            )}
            {nothingToMatch.length > 0 && (
              <p className="border-bank-border text-bank-attention-ink border-t px-4 py-2.5 text-[12.5px]">
                Nothing to match for {nothingToMatch.join(", ")} — no questions in the bank yet.
              </p>
            )}
          </>
        )}
        <p className="text-bank-muted border-bank-border border-t px-4 py-2 text-xs">
          A question serves this JD once it is approved. Interviews already started keep the questions they
          were given.
        </p>
      </Panel>

      {ticked.length > 0 && (
        <div className="border-bank-accent bg-bank-accent-wash sticky bottom-3 z-10 flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-lg border px-4 py-2 shadow-lg">
          <span className="text-bank-accent-ink font-mono text-xs font-semibold tabular-nums">
            {ticked.length} selected
          </span>
          <span className="text-bank-muted text-[12.5px]">
            Promoting approves them to the whole bank, so any interview may ask them.
          </span>
          <div className="ml-auto flex gap-1.5">
            <Button
              size="sm"
              variant="primary"
              disabled={promote.isPending}
              onClick={() => promote.mutate(ticked, { onSuccess: () => setChecked(new Set()) })}
            >
              Promote {ticked.length} to the whole bank
            </Button>
            <Button size="sm" onClick={() => setChecked(new Set())}>
              Clear
            </Button>
          </div>
        </div>
      )}

      {canManage && <AddQuestionsDrawer jd={jd} data={data} open={adding} onClose={() => setAdding(false)} />}
    </div>
  );
}
